import React, { useState, useRef, useEffect } from 'react';

interface SMSCodeInputProps {
  length?: number;
  onComplete: (code: string) => void;
  onResend?: () => void;
  isLoading?: boolean;
  phone?: string;
  countdown?: number;
}

export function SMSCodeInput({ 
  length = 6, 
  onComplete, 
  onResend, 
  isLoading = false,
  phone,
  countdown = 0
}: SMSCodeInputProps) {
  const [code, setCode] = useState<string[]>(Array(length).fill(''));
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (value: string, index: number) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Take only the last character
    setCode(newCode);

    // Move to next input
    if (value && index < length - 1) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    // Check if code is complete
    if (newCode.every(digit => digit !== '') && newCode.join('').length === length) {
      onComplete(newCode.join(''));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // Move to previous input if current is empty
        setActiveIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digits = paste.replace(/\D/g, '').slice(0, length);
    
    if (digits.length > 0) {
      const newCode = Array(length).fill('');
      for (let i = 0; i < digits.length && i < length; i++) {
        newCode[i] = digits[i];
      }
      setCode(newCode);
      
      if (digits.length === length) {
        onComplete(digits);
      } else {
        // Focus next empty input
        const nextIndex = Math.min(digits.length, length - 1);
        setActiveIndex(nextIndex);
        inputRefs.current[nextIndex]?.focus();
      }
    }
  };

  const formatPhone = (phone: string) => {
    if (phone.length === 11) {
      return `+7 ${phone.slice(1, 4)} ${phone.slice(4, 7)} ${phone.slice(7, 9)} ${phone.slice(9, 11)}`;
    }
    return phone;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Введите код из SMS</h2>
        <p className="text-gray-600">
          Мы отправили код подтверждения на номер{' '}
          <span className="font-medium text-gray-900">
            {phone ? formatPhone(phone) : ''}
          </span>
        </p>
      </div>

      {/* Code Input */}
      <div className="flex justify-center space-x-3">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[index]}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={() => setActiveIndex(index)}
            disabled={isLoading}
            className={`w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg transition-colors ${
              activeIndex === index
                ? 'border-emerald-500 ring-2 ring-emerald-200'
                : code[index]
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-gray-300'
            } ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            } focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200`}
          />
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Проверяем код...</span>
        </div>
      )}

      {/* Resend button */}
      {onResend && (
        <div className="text-center">
          {countdown > 0 ? (
            <p className="text-gray-500">
              Повторно отправить код можно через{' '}
              <span className="font-medium text-emerald-600">{countdown} сек</span>
            </p>
          ) : (
            <button
              onClick={onResend}
              disabled={isLoading}
              className="text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50"
            >
              Отправить код повторно
            </button>
          )}
        </div>
      )}

      {/* Help text */}
      <div className="text-center text-sm text-gray-500">
        <p>Не получили SMS? Проверьте правильность номера или попробуйте отправить повторно</p>
      </div>
    </div>
  );
}